/**
 * Phase 5c ÔÇö Achats lesen: Domain-Aggregat-Index + merged Blob.
 */

import {
  listAggregatePurchases,
  isDomainAggregateIndexEnabled
} from '../infrastructure/storage/domainAggregateStore';
import {
  listEntityPurchases,
  isEntityStorePrimaryReadEnabled,
  isEntityStoreReadEnabled
} from '../infrastructure/storage/entityAggregateStore';
import { readEntityOnly } from './canonicalEntityRead';
import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import { normalizePurchasesField, readArrayFieldFromMergedBlob, mergePurchasesById } from './businessStateSync';
import { normalizeBusinessStorageId } from './sharedCore';

/** @type {Map<string, object[]>} */
const purchasesReadCache = new Map();

function purchasesCacheKey(businessId) {
  if (!businessId) return null;
  return String(normalizeBusinessStorageId(businessId) || businessId);
}

export function readPurchasesFromMergedBlob(businessId) {
  return readArrayFieldFromMergedBlob(
    businessId,
    'purchases',
    getCachedPurchasesRead,
    normalizePurchasesField
  );
}

/**
 * @param {string} businessId
 * @returns {Promise<object[]>}
 */
export async function readPurchasesFromBestSource(businessId) {
  if (!businessId) return [];

  const fromBlob = readPurchasesFromMergedBlob(businessId);

  if (isEntityStoreNoKvBlobEnabled()) {
    const fromEntity = await readEntityOnly(businessId, listEntityPurchases);
    if (fromEntity.length > 0) return fromEntity;
    if (fromBlob.length > 0) {
      try {
        const { resyncAllEntityStoresFromBlob } = await import(
          '../infrastructure/storage/entityStoreMigration'
        );
        resyncAllEntityStoresFromBlob(businessId).catch(() => {});
      } catch {
        /* optional */
      }
      return fromBlob;
    }
    return fromEntity;
  }

  if (isEntityStorePrimaryReadEnabled()) {
    try {
      const fromEntity = await listEntityPurchases(businessId);
      if (fromEntity.length > 0) {
        if (fromBlob.length > 0) {
          return mergePurchasesById(fromEntity, fromBlob);
        }
        return fromEntity;
      }
    } catch {
      /* fallback below */
    }
  }

  if (fromBlob.length > 0) return fromBlob;

  if (isEntityStoreReadEnabled()) {
    try {
      const fromEntity = await listEntityPurchases(businessId);
      if (fromEntity.length > 0) return fromEntity;
    } catch {
      /* fallback below */
    }
  }

  if (!isDomainAggregateIndexEnabled()) return fromBlob;
  try {
    return await listAggregatePurchases(businessId);
  } catch {
    return fromBlob;
  }
}

export async function refreshPurchasesReadCache(businessId, preloaded) {
  const purchases =
    preloaded !== undefined
      ? Array.isArray(preloaded)
        ? preloaded
        : []
      : await readPurchasesFromBestSource(businessId);
  const key = purchasesCacheKey(businessId);
  if (key) {
    purchasesReadCache.set(key, purchases);
  }
  return purchases;
}

export function getCachedPurchasesRead(businessId) {
  const key = purchasesCacheKey(businessId);
  if (!key) return null;
  return (
    purchasesReadCache.get(key) ??
    purchasesReadCache.get(String(businessId)) ??
    purchasesReadCache.get(`business_${key}`) ??
    null
  );
}

export function clearPurchasesReadCache(businessId) {
  if (businessId) {
    const key = purchasesCacheKey(businessId);
    if (key) {
      purchasesReadCache.delete(key);
      purchasesReadCache.delete(String(businessId));
      purchasesReadCache.delete(`business_${key}`);
    }
  } else {
    purchasesReadCache.clear();
  }
}

export function seedPurchasesReadCache(businessId, purchases) {
  const key = purchasesCacheKey(businessId);
  if (!key || purchases === undefined) return;
  purchasesReadCache.set(
    key,
    Array.isArray(purchases) ? purchases : normalizePurchasesField(purchases)
  );
}

/** Synchroner Lesepfad (Cache ∪ Blob, Blob-Priorität). */
export function readPurchasesSyncForBusiness(businessId) {
  if (!businessId) return [];
  const fromBlob = readPurchasesFromMergedBlob(businessId);
  const cached = getCachedPurchasesRead(businessId);
  if (fromBlob.length > 0) {
    if (cached && cached.length > 0) {
      return mergePurchasesById(cached, fromBlob);
    }
    return fromBlob;
  }
  if (cached && cached.length > 0) return cached;
  return fromBlob;
}
